import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabaseService';
import { BackendUser, fetchUserProfile } from '../user/userService';
import { useAlternativeLinkReviewStore } from '../../stores/appAlternativeLinkReviewStore';
import { useAnalysisStore } from '../../stores/appAnalysisStore';
import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { getDemoSession, exitDemoSession } from '../accountSetup/demoSession';
import { useAppSettingsStore } from '../../stores/appSettingsStore';
import { sourcingAccessContext, useSourcingRunStore } from '../../stores/appSourcingRunStore';
import { isImpersonating } from '../../stores/appImpersonationStore';

type AuthContextType = {
  user: User | null;
  backendUser: BackendUser | null;
  accountSettings: string[];
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  refreshProfile: (background?: boolean) => Promise<void>;
};
const AuthContext = createContext<AuthContextType>({ user: null, backendUser: null, accountSettings: [], loading: true, setUser: () => {}, refreshProfile: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);
  const [accountSettings, setAccountSettings] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const actorId = useRef<string | null>(null);
  const generation = useRef(0);
  const previousMode = useRef<string | null>(null);
  const previousSourcingAccess = useRef<string | null>(null);

  const loadProfile = useCallback(async (background = false) => {
    const ticket = ++generation.current;
    try {
      const profile = await fetchUserProfile();
      if (ticket !== generation.current) return;
      const sourcingAccess = sourcingAccessContext(actorId.current, profile, []);
      if (previousSourcingAccess.current !== sourcingAccess) useSourcingRunStore.getState().clear();
      previousSourcingAccess.current = sourcingAccess;
      const mode = `${profile.id}:${Boolean(profile.demoContext)}:${profile.accountSettings?.includes('account_setup') ?? false}:${profile.demoContext?.restaurantIds.slice().sort().join(',') ?? ''}:${profile.impersonation_view ?? ''}`;
      if (previousMode.current !== null && previousMode.current !== mode) {
        window.location.reload(); return;
      }
      previousMode.current = mode;
      useAppSettingsStore.getState().initializeAccountLanguage(
        profile.managed_customer && !profile.demoContext && !isImpersonating() ? profile.id : null,
        profile.preferred_language
      );
      setBackendUser(profile);
      setAccountSettings(profile.accountSettings || []);
    } catch {
      if (ticket !== generation.current) return;
      // A transient polling failure must not unmount active forms. Server guards
      // still enforce access, and the next successful check applies mode changes.
      if (background && previousMode.current !== null) return;
      // A failed refresh must not turn a restricted account into an unrestricted one.
      useSourcingRunStore.getState().clear();
      previousSourcingAccess.current = null;
      setBackendUser(null);
      setAccountSettings([]);
    }
    // Only the latest request settles loading: a superseded one would end it before any profile exists.
    setLoading(false);
  }, []);

  const invalidateProfile = useCallback(() => { generation.current++; }, []);

  useEffect(() => {
    let disposed = false;
    const acceptUser = async (nextUser: User | null) => {
      if (disposed) return;
      if (actorId.current !== (nextUser?.id ?? null)) {
        useAppSettingsStore.getState().initializeAccountLanguage(null);
        useAlternativeLinkReviewStore.getState().clear();
        useAnalysisStore.getState().clear();
        useCostPriceStore.getState().setIngredientClipboard(null);
        useSourcingRunStore.getState().clear();
        previousSourcingAccess.current = null;
        generation.current++;
        previousMode.current = null;
        setBackendUser(null);
        setAccountSettings([]);
      }
      actorId.current = nextUser?.id ?? null;
      setUser(nextUser);
      const demo = getDemoSession();
      if (demo && demo.actorId !== nextUser?.id) { exitDemoSession(); return; }
      if (nextUser) await loadProfile();
      else if (!disposed) setLoading(false);
    };
    if (process.env.REACT_APP_MOCK_AUTH === 'true') {
      void acceptUser({ id: process.env.REACT_APP_MOCK_USER_ID || '', email: process.env.REACT_APP_MOCK_USER_EMAIL || 'mock@example.com' } as User);
    } else {
      void supabase.auth.getSession().then(({ data }) => acceptUser(data.session?.user ?? null));
    }
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      // Avoid invoking Supabase-backed service reads synchronously inside its auth lock.
      window.setTimeout(() => { void acceptUser(session?.user ?? null); }, 0);
    });
    let refreshingAccess = false;
    const refreshAccess = (background = false) => {
      if (!actorId.current || refreshingAccess) return;
      refreshingAccess = true;
      void loadProfile(background).finally(() => { refreshingAccess = false; });
    };
    const focus = () => refreshAccess();
    const visibility = () => { if (document.visibilityState === 'visible') focus(); };
    // Employee release/re-enable changes must reach an already-open customer tab too.
    const accessInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshAccess(true);
    }, 10000);
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true;
      invalidateProfile();
      data.subscription.unsubscribe();
      window.clearInterval(accessInterval);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [loadProfile, invalidateProfile]);

  return <AuthContext.Provider value={{ user, backendUser, accountSettings, loading, setUser, refreshProfile: loadProfile }}>{children}</AuthContext.Provider>;
}
export function useAuth() { return useContext(AuthContext); }
